"use client";

import React, { useEffect, useMemo, useState } from "react";
import {
  Calendar,
  Check,
  ExternalLink,
  FileSpreadsheet,
  FileText,
  Grid3X3,
  HardDrive,
  Library,
  Loader2,
  Mail,
  Search,
  Sparkles,
  Wrench,
  X,
} from "lucide-react";
import { Input } from "../ui/input";
import useKnowledgeBases from "../../hooks/useKnowledgeBases";
import IntegrationService from "../../services/integration.service";
import { useOrganization } from "../../contexts/OrganizationContext";

const PROVIDER_META = {
  all: { label: "All", icon: Grid3X3 },
  gmail: {
    label: "Gmail",
    image: "https://ssl.gstatic.com/images/branding/product/1x/gmail_2020q4_48dp.png",
    fallbackIcon: Mail,
  },
  drive: {
    label: "Drive",
    image: "https://ssl.gstatic.com/images/branding/product/1x/drive_2020q4_48dp.png",
    fallbackIcon: HardDrive,
  },
  calendar: {
    label: "Calendar",
    image: "https://ssl.gstatic.com/images/branding/product/1x/calendar_2020q4_48dp.png",
    fallbackIcon: Calendar,
  },
  sheets: {
    label: "Sheets",
    image: "https://ssl.gstatic.com/images/branding/product/1x/sheets_2020q4_48dp.png",
    fallbackIcon: FileSpreadsheet,
  },
  docs: {
    label: "Docs",
    image: "https://ssl.gstatic.com/images/branding/product/1x/docs_2020q4_48dp.png",
    fallbackIcon: FileText,
  },
  whatsapp: {
    label: "WhatsApp",
    image: "https://cdn.simpleicons.org/whatsapp/25D366",
    fallbackIcon: Sparkles,
  },
  shopify: {
    label: "Shopify",
    image: "https://cdn.simpleicons.org/shopify/95BF47",
    fallbackIcon: Sparkles,
  },
  creative: { label: "Creative", icon: Sparkles },
  knowledge: { label: "Knowledge", icon: Library },
  utilities: { label: "Utilities", icon: Wrench },
  other: { label: "Other", icon: Sparkles },
};

// Friendly image model names the owner can allow for the image_generation
// skill. Mirrors the backend catalog (image_models seed v149); keep in sync.
const IMAGE_GENERATION_SKILL = "image_generation";
const IMAGE_MODEL_OPTIONS = [
  "Nano Banana",
  "Nano Banana 2",
  "Nano Banana Pro",
  "GPT-5 Image Mini",
  "GPT-5 Image",
  "GPT-5.4 Image 2",
];
const DEFAULT_IMAGE_MODEL = "Nano Banana";

// Drop per-skill settings for skills that are no longer selected, so we don't
// persist config for a skill the assistant doesn't have.
export function pruneSkillSettings(skillSettings, selectedSkills) {
  if (!skillSettings || typeof skillSettings !== "object") return {};
  const selected = Array.isArray(selectedSkills) ? selectedSkills : [];
  const out = {};
  for (const [skillName, settings] of Object.entries(skillSettings)) {
    if (selected.includes(skillName)) out[skillName] = settings;
  }
  return out;
}

// Drop skill→integration bindings for skills that are no longer selected, and
// any empty values, so we only persist live bindings.
export function pruneSkillIntegrations(skillIntegrations, selectedSkills) {
  if (!skillIntegrations || typeof skillIntegrations !== "object") return {};
  const selected = Array.isArray(selectedSkills) ? selectedSkills : [];
  const out = {};
  for (const [skillName, integrationId] of Object.entries(skillIntegrations)) {
    if (selected.includes(skillName) && integrationId) out[skillName] = integrationId;
  }
  return out;
}

// Selected skills that act through a connected account (Google/Meta/Shopify)
// but don't yet have an Integration ID bound in `skillIntegrations`. The owner
// must pick which account each such skill uses, so callers (create flow / config
// view) use this to block "Next"/Save until every binding is resolved.
export function getUnboundIntegrationSkills(selectedSkills, skills, skillIntegrations) {
  const selected = Array.isArray(selectedSkills) ? selectedSkills : [];
  const safeSkills = Array.isArray(skills) ? skills : [];
  const bindings = skillIntegrations || {};

  return selected
    .map((name) => {
      const skill = safeSkills.find((s) => s?.name === name);
      if (!skill) return null;
      return { ...skill, providerType: detectProvider(skill) };
    })
    .filter((skill) => skill && skillNeedsIntegration(skill) && !bindings[skill.name]);
}

function ImageModelSettings({ settings, onChange }) {
  const allowed = Array.isArray(settings?.allowed_models) && settings.allowed_models.length > 0
    ? settings.allowed_models
    : [DEFAULT_IMAGE_MODEL];
  const defaultModel = settings?.default_model && allowed.includes(settings.default_model)
    ? settings.default_model
    : allowed[0];

  const toggleModel = (model) => {
    let nextAllowed;
    if (allowed.includes(model)) {
      // Keep at least one model allowed.
      if (allowed.length === 1) return;
      nextAllowed = allowed.filter((m) => m !== model);
    } else {
      nextAllowed = [...allowed, model];
    }
    const nextDefault = nextAllowed.includes(defaultModel) ? defaultModel : nextAllowed[0];
    onChange({ allowed_models: nextAllowed, default_model: nextDefault });
  };

  const setDefault = (model) => {
    if (!allowed.includes(model)) return;
    onChange({ allowed_models: allowed, default_model: model });
  };

  return (
    <div className="space-y-3 rounded-xl border border-purple-500/30 bg-purple-500/5 p-3">
      <div>
        <p className="text-sm font-semibold text-white">Image models</p>
        <p className="text-xs text-gray-400">
          Choose which models this agent may use to generate images, and the default.
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        {IMAGE_MODEL_OPTIONS.map((model) => {
          const isAllowed = allowed.includes(model);
          return (
            <button
              key={model}
              type="button"
              onClick={() => toggleModel(model)}
              className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs transition-all ${
                isAllowed
                  ? "border-purple-500/50 bg-purple-600/20 text-purple-100"
                  : "border-white/10 bg-black/30 text-gray-300 hover:border-white/30"
              }`}
            >
              {isAllowed && <Check className="h-3 w-3" />}
              {model}
            </button>
          );
        })}
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs text-gray-400">Default:</span>
        <select
          value={defaultModel}
          onChange={(e) => setDefault(e.target.value)}
          className="rounded-lg border border-white/20 bg-black/50 px-2 py-1 text-xs text-white"
        >
          {allowed.map((model) => (
            <option key={model} value={model}>{model}</option>
          ))}
        </select>
      </div>
    </div>
  );
}

// The query_knowledge_base skill lets the owner assign exactly one knowledge
// base the assistant may search. The KB is chosen here (at tool-assignment),
// never by the LLM — the tool only takes a query + result count.
const QUERY_KNOWLEDGE_BASE_SKILL = "query_knowledge_base";

function KnowledgeBaseSettings({ settings, onChange }) {
  const { knowledgeBases, loading, error } = useKnowledgeBases();
  const selectedId = settings?.knowledge_base_id || "";
  // Only ready KBs can be searched; the backend rejects assigning others.
  const readyKnowledgeBases = (knowledgeBases || []).filter(
    (kb) => String(kb?.status).toLowerCase() === "ready"
  );

  return (
    <div className="space-y-3 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3">
      <div>
        <p className="text-sm font-semibold text-white">Knowledge base</p>
        <p className="text-xs text-gray-400">
          Choose the single knowledge base this agent can search.
        </p>
      </div>
      {loading ? (
        <p className="text-xs text-gray-400">Loading knowledge bases…</p>
      ) : error ? (
        <p className="text-xs text-red-300">Could not load knowledge bases.</p>
      ) : readyKnowledgeBases.length === 0 ? (
        <p className="text-xs text-amber-300">
          No ready knowledge bases yet. Create one in the Knowledge section first.
        </p>
      ) : (
        <select
          value={selectedId}
          onChange={(e) =>
            onChange(
              e.target.value ? { knowledge_base_id: e.target.value } : {}
            )
          }
          className="w-full rounded-lg border border-white/20 bg-black/50 px-2 py-1.5 text-xs text-white"
        >
          <option value="">Select a knowledge base…</option>
          {readyKnowledgeBases.map((kb) => (
            <option key={kb.id} value={kb.id}>
              {kb.name}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}

const COMING_SOON_SKILLS = [
  {
    id: "coming-soon-whatsapp-send-message",
    name: "whatsapp_send_message",
    friendly_name: "WhatsApp Send Message",
    description: "Send outbound messages to WhatsApp contacts.",
    providerType: "whatsapp",
    comingSoon: true,
  },
  {
    id: "coming-soon-whatsapp-read-messages",
    name: "whatsapp_read_messages",
    friendly_name: "WhatsApp Read Messages",
    description: "Read incoming messages from WhatsApp chats.",
    providerType: "whatsapp",
    comingSoon: true,
  },
  {
    id: "coming-soon-shopify-orders",
    name: "shopify_orders",
    friendly_name: "Shopify Order Tracker",
    description: "Retrieve and track Shopify order lifecycle events.",
    providerType: "shopify",
    comingSoon: true,
  },
  {
    id: "coming-soon-shopify-products",
    name: "shopify_product_catalog",
    friendly_name: "Shopify Product Catalog",
    description: "Search products, variants, and inventory from Shopify.",
    providerType: "shopify",
    comingSoon: true,
  },
];

function ProviderIcon({ providerKey, className = "h-4 w-4" }) {
  const meta = PROVIDER_META[providerKey] || PROVIDER_META.other;
  const [imageFailed, setImageFailed] = useState(false);

  React.useEffect(() => {
    setImageFailed(false);
  }, [providerKey]);

  if (meta.image && !imageFailed) {
    return (
      <img
        src={meta.image}
        alt={meta.label}
        className={`${className} block shrink-0 object-contain align-middle`}
        loading="lazy"
        onError={() => setImageFailed(true)}
      />
    );
  }

  const Icon = meta.fallbackIcon || meta.icon || Sparkles;
  return <Icon className={className} />;
}

// Detect a specific integration/product from a free-text string.
function detectIntegration(text) {
  if (/(whatsapp|meta)/.test(text)) return "whatsapp";
  if (text.includes("shopify")) return "shopify";
  if (/(^|[_\s-])gmail|google\s*mail/.test(text)) return "gmail";
  if (/(^|[_\s-])sheets?\b|google\s*sheets?|spreadsheet/.test(text)) return "sheets";
  if (/(^|[_\s-])docs?\b|google\s*docs?/.test(text)) return "docs";
  if (/(^|[_\s-])drive\b|google\s*drive/.test(text)) return "drive";
  if (/(^|[_\s-])calendar|google\s*calendar/.test(text)) return "calendar";
  return null;
}

export function detectProvider(skill) {
  // 1) Prefer explicit provider/integration fields (most reliable)
  const explicit = String(
    skill?.provider || skill?.service || skill?.service_type || skill?.integration || ""
  ).toLowerCase();
  const fromExplicit = detectIntegration(explicit);
  if (fromExplicit) return fromExplicit;

  // 2) Infer from the skill name / friendly name / description
  const haystack = `${skill?.name || ""} ${skill?.friendly_name || ""} ${skill?.description || ""}`.toLowerCase();
  const fromHaystack = detectIntegration(haystack);
  if (fromHaystack) return fromHaystack;

  // 3) Fall back to the backend-provided capability category
  const category = String(skill?.category || "").toLowerCase();
  if (category === "creative") return "creative";
  if (category === "knowledge") return "knowledge";
  if (category === "utilities" || category === "utility") return "utilities";

  return "other";
}

// Skills in these provider groups act through a connected account, so the
// owner must pick which Integration ID the assistant should use. Google
// services map 1:1 to a service_type; Meta/Shopify use the single connection.
export const PROVIDER_INTEGRATION = {
  gmail: { type: "google", service: "gmail" },
  drive: { type: "google", service: "drive" },
  calendar: { type: "google", service: "calendar" },
  sheets: { type: "google", service: "sheets" },
  docs: { type: "google", service: "docs" },
  whatsapp: { type: "meta" },
  shopify: { type: "shopify" },
};

const GOOGLE_SERVICE_LABEL = {
  gmail: "Gmail",
  calendar: "Google Calendar",
  drive: "Google Drive",
  sheets: "Google Sheets",
  docs: "Google Docs",
};

export const PLATFORM_LABEL = { google: "Google", meta: "Meta", shopify: "Shopify" };

function skillNeedsIntegration(skill) {
  return Boolean(skill && PROVIDER_INTEGRATION[skill.providerType]);
}

// Build the selectable integrations for a provider from the unified
// { meta, google, shopify } payload returned by IntegrationService.
export function integrationOptionsFor(providerType, integrations) {
  const meta = PROVIDER_INTEGRATION[providerType];
  if (!meta) return [];

  if (meta.type === "google") {
    return (integrations.google || [])
      .filter((i) => i.service_type === meta.service)
      .map((i) => ({
        id: i.id,
        label: i.google_account_email
          ? `${GOOGLE_SERVICE_LABEL[i.service_type] || i.service_type} · ${i.google_account_email}`
          : GOOGLE_SERVICE_LABEL[i.service_type] || i.service_type,
      }));
  }

  if (meta.type === "meta") {
    return (integrations.meta || []).map((i) => ({
      id: i.id,
      label:
        i.whatsapp_info?.verified_name ||
        i.whatsapp_info?.phone_number ||
        i.business_name ||
        i.name ||
        i.id,
    }));
  }

  if (meta.type === "shopify") {
    return (integrations.shopify || []).map((i) => ({
      id: i.id,
      label: i.shop_name || i.shop_domain || i.id,
    }));
  }

  return [];
}

// Per-skill picker that binds a connected Integration ID to a provider skill
// (e.g. Google Docs → which Docs account). The selection persists in the
// assistant's `skillIntegrations` map (skill name → integration UUID), which the
// backend reads for per-skill credential binding — separate from skillSettings.
function IntegrationSettings({ skill, integrations, loading, integrationId, onChange }) {
  const meta = PROVIDER_INTEGRATION[skill.providerType];
  const options = integrationOptionsFor(skill.providerType, integrations);
  const selectedId = integrationId || "";
  const providerLabel =
    PROVIDER_META[skill.providerType]?.label || PLATFORM_LABEL[meta.type] || "integration";

  return (
    <div className="space-y-2 rounded-xl border border-sky-500/30 bg-sky-500/5 p-3">
      <div className="flex items-center gap-2">
        <ProviderIcon providerKey={skill.providerType} className="h-4 w-4" />
        <p className="text-sm font-semibold text-white">{skill.friendly_name || skill.name}</p>
      </div>
      <p className="text-xs text-gray-400">
        Choose which {providerLabel} integration this skill should use.
      </p>

      {loading ? (
        <p className="flex items-center gap-1.5 text-xs text-gray-400">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading integrations…
        </p>
      ) : options.length > 0 ? (
        <select
          value={selectedId}
          onChange={(e) => onChange(e.target.value || null)}
          className="w-full rounded-lg border border-white/20 bg-black/50 px-2 py-1.5 text-xs text-white"
        >
          <option value="">Select a {providerLabel} integration…</option>
          {options.map((opt) => (
            <option key={opt.id} value={opt.id}>
              {opt.label}
            </option>
          ))}
        </select>
      ) : (
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs text-amber-300">No {providerLabel} integration connected yet.</p>
          <a
            href="/dashboard?view=Integrations"
            target="_blank"
            rel="noreferrer"
            className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-white/20 bg-black/40 px-2 py-1 text-xs text-gray-200 transition-colors hover:border-white/35"
          >
            <ExternalLink className="h-3 w-3" /> Connect
          </a>
        </div>
      )}

      {selectedId && (
        <p className="break-all font-mono text-[10px] text-gray-500">ID: {selectedId}</p>
      )}
    </div>
  );
}

export default function SkillStoreSelector({ skills, selectedSkills, onChange, skillSettings, onSkillSettingsChange, skillIntegrations, onSkillIntegrationChange }) {
  const { currentAccount } = useOrganization();
  const [activeProvider, setActiveProvider] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [integrations, setIntegrations] = useState({ meta: [], google: [], shopify: [] });
  const [loadingIntegrations, setLoadingIntegrations] = useState(false);

  const normalizedSkills = useMemo(() => {
    const safe = Array.isArray(skills) ? skills : [];
    const fromApi = safe.map((skill) => ({
      ...skill,
      providerType: detectProvider(skill),
      id: skill?.name || skill?.friendly_name || `skill-${Math.random().toString(36).slice(2)}`,
    }));

    return [...fromApi, ...COMING_SOON_SKILLS];
  }, [skills]);

  const providerCounts = useMemo(() => {
    const base = {
      all: normalizedSkills.length,
      gmail: 0,
      drive: 0,
      calendar: 0,
      sheets: 0,
      docs: 0,
      whatsapp: 0,
      shopify: 0,
      creative: 0,
      knowledge: 0,
      utilities: 0,
      other: 0,
    };
    normalizedSkills.forEach((skill) => {
      base[skill.providerType] = (base[skill.providerType] || 0) + 1;
    });
    return base;
  }, [normalizedSkills]);

  const filteredSkills = useMemo(() => {
    const term = searchQuery.trim().toLowerCase();

    return normalizedSkills.filter((skill) => {
      const byProvider = activeProvider === "all" || skill.providerType === activeProvider;
      if (!byProvider) return false;

      if (!term) return true;

      const text = `${skill?.friendly_name || ""} ${skill?.name || ""} ${skill?.description || ""}`.toLowerCase();
      return text.includes(term);
    });
  }, [activeProvider, normalizedSkills, searchQuery]);

  const selected = Array.isArray(selectedSkills) ? selectedSkills : [];

  // Selected skills that act through a connected account and therefore need an
  // Integration ID assigned (Google services, Meta, Shopify).
  const integrationSkills = useMemo(() => {
    const sel = Array.isArray(selectedSkills) ? selectedSkills : [];
    return sel
      .map((name) => normalizedSkills.find((s) => s.name === name))
      .filter(skillNeedsIntegration);
  }, [selectedSkills, normalizedSkills]);

  // Only fetch the account's integrations once a skill that needs one is picked.
  useEffect(() => {
    if (integrationSkills.length === 0 || !currentAccount?.id) return;
    let cancelled = false;
    setLoadingIntegrations(true);
    IntegrationService.listAllIntegrations(currentAccount.id)
      .then((result) => {
        if (!cancelled && result.success && result.data) setIntegrations(result.data);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoadingIntegrations(false);
      });
    return () => {
      cancelled = true;
    };
  }, [integrationSkills.length, currentAccount?.id]);

  const toggleSkill = (skill) => {
    const skillName = skill?.name;
    if (!skillName) return;
    if (skill?.comingSoon) return;

    if (selected.includes(skillName)) {
      onChange(selected.filter((item) => item !== skillName));
    } else {
      onChange([...selected, skillName]);
    }
  };

  return (
    <div className="space-y-4 rounded-2xl border border-white/15 bg-white/5 p-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-white">Skill Store</p>
          <p className="text-xs text-gray-400">Choose tools by integration and capability.</p>
        </div>
        <span className="rounded-full border border-white/15 bg-black/40 px-3 py-1 text-xs text-gray-300">
          {selected.length} selected
        </span>
      </div>

      <div className="flex flex-wrap gap-2">
        {Object.keys(PROVIDER_META).map((providerKey) => {
          const meta = PROVIDER_META[providerKey];
          const isActive = activeProvider === providerKey;

          return (
            <button
              key={providerKey}
              type="button"
              onClick={() => setActiveProvider(providerKey)}
              className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs transition-all duration-200 ${
                isActive
                  ? "border-purple-500/50 bg-purple-600/20"
                  : "border-white/10 bg-black/30 hover:border-white/30"
              }`}
            >
              <ProviderIcon providerKey={providerKey} className="h-3.5 w-3.5" />
              <span className="font-medium text-white whitespace-nowrap">{meta.label}</span>
              <span className="text-gray-400">{providerCounts[providerKey] || 0}</span>
            </button>
          );
        })}
      </div>

      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search skills..."
          className="border-white/20 bg-black/50 pl-9 text-white placeholder-gray-500"
        />
      </div>

      {selected.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selected.map((skillName) => {
            const skill = normalizedSkills.find((item) => item.name === skillName);
            return (
              <span
                key={skillName}
                className="inline-flex items-center gap-1 rounded-full border border-purple-500/40 bg-purple-500/20 px-2 py-1 text-xs text-purple-200"
              >
                {skill?.friendly_name || skillName}
                <button
                  type="button"
                  onClick={() => toggleSkill(skill || { name: skillName })}
                  className="rounded-full p-0.5 transition-colors hover:bg-purple-500/30"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            );
          })}

          <button
            type="button"
            onClick={() => onChange([])}
            className="rounded-full border border-white/20 bg-black/40 px-2 py-1 text-xs text-gray-300 hover:border-white/35"
          >
            Clear all
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 gap-2 pr-1 md:grid-cols-2 lg:grid-cols-3 max-h-[220px] overflow-y-auto">
        {filteredSkills.map((skill) => {
          const isSelected = selected.includes(skill.name);
          const isComingSoon = Boolean(skill.comingSoon);
          const providerLabel = PROVIDER_META[skill.providerType]?.label || "Other";

          return (
            <button
              key={skill.id}
              type="button"
              onClick={() => toggleSkill(skill)}
              disabled={isComingSoon}
              className={`rounded-xl border p-3 text-left transition-all duration-200 ${
                isSelected
                  ? "border-purple-500/50 bg-purple-600/20 shadow-lg shadow-purple-500/10"
                  : isComingSoon
                  ? "border-white/10 bg-black/30 opacity-70 cursor-not-allowed"
                  : "border-white/10 bg-black/35 hover:border-white/30"
              }`}
            >
              <div className="mb-2 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-white">{skill.friendly_name || skill.name}</p>
                  <p className="mt-1 line-clamp-2 text-xs text-gray-400">{skill.description || skill.name}</p>
                </div>

                {isComingSoon ? (
                  <span className="shrink-0 rounded-full bg-white/10 px-2 py-1 text-[10px] uppercase tracking-wide text-gray-300">
                    Coming soon
                  </span>
                ) : isSelected ? (
                  <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-white/10 px-2 py-1 text-[10px] uppercase tracking-wide text-gray-200">
                    <Check className="h-3 w-3" />
                    Added
                  </span>
                ) : (
                  <span className="shrink-0 rounded-full bg-white/10 px-2 py-1 text-[10px] uppercase tracking-wide text-gray-300">
                    Add
                  </span>
                )}
              </div>

              <span className="inline-flex h-6 items-center rounded-full border border-white/10 bg-black/35 px-2 text-gray-300">
                <span className="inline-flex items-center gap-1.5 leading-none">
                  <span className="inline-flex h-3.5 w-3.5 items-center justify-center">
                    <ProviderIcon providerKey={skill.providerType} className="h-3.5 w-3.5" />
                  </span>
                  <span className="text-[11px] font-medium leading-none">{providerLabel}</span>
                </span>
              </span>
            </button>
          );
        })}

        {filteredSkills.length === 0 && (
          <div className="rounded-xl border border-white/10 bg-black/35 p-3 text-xs text-gray-400">
            No skills found for this category.
          </div>
        )}
      </div>

      {selected.includes(IMAGE_GENERATION_SKILL) && typeof onSkillSettingsChange === "function" && (
        <ImageModelSettings
          settings={skillSettings?.[IMAGE_GENERATION_SKILL]}
          onChange={(next) => onSkillSettingsChange(IMAGE_GENERATION_SKILL, next)}
        />
      )}

      {selected.includes(QUERY_KNOWLEDGE_BASE_SKILL) && typeof onSkillSettingsChange === "function" && (
        <KnowledgeBaseSettings
          settings={skillSettings?.[QUERY_KNOWLEDGE_BASE_SKILL]}
          onChange={(next) => onSkillSettingsChange(QUERY_KNOWLEDGE_BASE_SKILL, next)}
        />
      )}

      {typeof onSkillIntegrationChange === "function" &&
        integrationSkills.map((skill) => (
          <IntegrationSettings
            key={skill.name}
            skill={skill}
            integrations={integrations}
            loading={loadingIntegrations}
            integrationId={skillIntegrations?.[skill.name]}
            onChange={(id) => onSkillIntegrationChange(skill.name, id)}
          />
        ))}
    </div>
  );
}
